import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DynamicDropdown, { DynamicDropdownPresentationProps } from "@insite/mobius/DynamicDropdown";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Circle from "@tIntermatic/Icons/Circle";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";


const mapStateToProps = (state: ApplicationState) => ({
    pagination: getProductListDataViewProperty(state, "pagination"),
    productsDataView: getProductListDataView(state),
    searchQuery: state.pages.productList.parameter?.search,
    searchWithinQuery: state.pages.productList?.filteredApiParameter?.searchWithin,
    currentLanguage: state.context.session.language,
    currentWebsiteName: state.context.website.name,
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListSortSelectStyles {
    dropdownSelect?: DynamicDropdownPresentationProps;
    iconElementEnabled?: IconPresentationProps;
    labelProps?: InjectableCss;
    labelPropsMobile?: InjectableCss;
    wrapper?: InjectableCss;
}

export const sortSelectStyles: ProductListSortSelectStyles = {
    wrapper: {
        css: css`
            display: flex;
            > span[class^="TypographyStyle"] {
                width: 35%;
                @media all and (max-width: 991px) {
                    width: 11%;
                }
                @media all and (max-width: 767px) {
                    width: 76px;
                }
                @media all and (max-width: 480px) {
                    width: 90px;
                }
            }
            @media all and (max-width: 991px) and (min-width: 0px) {
                width: 100%;
            }
        `,
    },
    iconElementEnabled: {
        css: css`
            margin-left: -3px;
            > svg {
                fill: #006bc0;
                stroke: #006bc0;
            }
        `,
    },
    dropdownSelect: {
        cssOverrides: {
            dropdownWrapper: css`
                width: 100%;
                max-width: 100%;
                border: 1px solid #cccccc;
                @media all and (max-width: 991px) and (min-width: 0px) {
                    border: none;
                    max-width: calc(100% + 90px);
                    @media all and (max-width: 768px) {
                        margin-left: 0;
                    }
                }

                > span {
                    text-transform: uppercase;
                    @media all and (max-width: 991px) and (min-width: 0px) {
                        font-size: 15px;
                        font-weight: 600;
                        letter-spacing: 0;
                        line-height: 23px;
                        padding-left: 5px;
                        @media all and (max-width: 768px) {
                            margin-left: 1%;
                        }
                    }
                }
                span[class^="Typography"] {
                    padding-right: 36px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    padding: 9px 9px 9px 0;
                                    margin-left: 0;
                                `,
                                css`
                                    padding: 9px 9px 9px 0;
                                    margin-left: 0;
                                `,
                                css`
                                    padding: 9px 9px 9px 0;
                                    margin-left: 0;
                                `,
                                null,
                            ],
                            "minMax",
                        );
                    }}
                }
                span[class^="IconWrapper"] {
                    left: inherit;
                    color: #33333a;
                    @media all and (max-width: 991px) and (min-width: 0px) {
                        > svg {
                            stroke-width: 1;
                        }
                    }
                }
                > ul {
                    box-sizing: border-box;
                    width: 202px;
                    border: 1px solid #e5e5e5;
                    border-radius: 6px;
                    box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.1);
                    text-transform: capitalize;
                    top: -4px;
                    left: -1px;
                    padding: 9px 0 9px 0;
                    background: rgb(255, 255, 255);
                    @media all and (max-width: 991px) and (min-width: 0px) {
                        width: 100%;
                    }
                    > div {
                        background-color: #fff;
                        color: #33333a;
                        font-size: 16px;
                        letter-spacing: 1.33px;
                        line-height: 30px;
                        box-sizing: border-box;
                        padding: 0 0 0 14px;
                        &:hover {
                            background-color: #e5e5e5;
                            color: #33333a;
                        }
                        > div {
                            margin-left: 5px;
                            > span[class^="IconWrapper"] {
                                margin-left: -8px;
                            }
                        }
                    }
                }
                div[class^="divElement"] {
                    display: inline-block;
                    margin-left: 10px;
                }
            `,
            inputSelect: css`
                width: 100%;
                font-weight: 300;
                width: 100%;
                background-color: #fff;
                border: none;
                font-size: 16px;
                letter-spacing: 0;
                line-height: 20px;
                caret-color: transparent;
                cursor: pointer;
                &:focus {
                    border: none;
                }
            `,
        },
    },

    labelProps: {
        css: css`
            width: inherit;
            color: #33333a;
            font-size: 16px;
            letter-spacing: 0;
            line-height: 19px;
            text-transform: uppercase;
            font-weight: 400;
            display: flex;
            align-self: center;
            @media all and (max-width: 991px) and (min-width: 0px) {
                display: none;
            }
        `,
    },

    labelPropsMobile: {
        css: css`
            font-size: 15px;
            font-weight: 600;
            letter-spacing: 0;
            line-height: 20px;
            display: flex;
            align-self: center;
            color: #33333a;
            text-transform: uppercase;
            width: 86px;
            z-index: 10;
            @media (min-width: 992px) {
                display: none;
            }
        `,
    },
};

const styles = sortSelectStyles;

const ProductListSortSelect = ({
    addProductFilters,
    pagination,
    productsDataView,
    searchQuery,
    searchWithinQuery,
    currentLanguage,
    currentWebsiteName
}: Props) => {
    if (!pagination) {
        return null;
    }

    const onChangeSortHandler = (event: string | undefined) => {
        if (event) {
            addProductFilters({ sort: event });
        }
    };
    const options: any = [];
    pagination.sortOptions.filter(sortOpt =>
        !(!(currentWebsiteName.toLowerCase() === "main" &&
        currentLanguage?.languageCode.toLowerCase() === "en") && (sortOpt.displayName === "Price: Low to High" || sortOpt.displayName === "Price: High to Low")),)?.map((ele : any) => {
            options.push({
                optionText: ele.displayName,
                optionValue: ele.sortType,
                rowChildren: (
                    <div>
                        {pagination.sortType === ele.sortType && (
                            <Icon {...styles.iconElementEnabled} src={Circle} size={8} />
                        )}
                        <div className="divElement">{ele.displayName}</div>
                    </div>
                ),
            });
    });

    const displayView = () => {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.labelProps}>{translate("Sort by")}</Typography>
                <Typography {...styles.labelPropsMobile}>{translate("Sort by:")}</Typography>

                <DynamicDropdown
                    {...styles.dropdownSelect}
                    options={options}
                    selected={pagination.sortType}
                    onSelectionChange={onChangeSortHandler}
                />
            </StyledWrapper>
        );
    };

    const conditionalView = () => {
        if (searchWithinQuery) {
            return displayView();
        }
        if (searchQuery && productsDataView.value?.length === 0) {
            return null;
        }
        return displayView();
    };

    return conditionalView();
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListSortSelect),
    definition: {
        group: "Product List",
        displayName: "Sort Select",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
